<script lang="ts">
  import { onMount } from 'svelte';
  import { userStore, uiState, setUIState, setUserId, searchStore, searchByPermissionStore, sortingStore } from '$lib/stores/userStore';
  import { DataHandler, type State, type Row } from '@vincjo/datatables/remote';
  import RowsPerPage from '$lib/components/datatable/RowsPerPage.svelte';
  import Pagination from '$lib/components/datatable/Pagination.svelte';
  import { ColumnNames } from '$lib/models/user/types';
  import SortButton, { type ListItem } from '$lib/components/common/sortButton/SortButton.svelte';
  import SearchBox from '$lib/components/common/searchBox/SearchBox.svelte';
  import Checkbox from '$lib/components/common/checkbox/Checkbox.svelte';
  import { createSearchTableStore } from '$lib/stores/searchTableStore';
  import { clickOutside } from '$lib/utils/clickOutside/clickOutsideUtility';
  import { DotsThreeOutlineVertical, Ghost, PencilSimple, Eye, EyeSlash } from 'phosphor-svelte';
  import { Icons } from '$lib/components/common/icon';

  export let clientId: number;

  const tableStore = createSearchTableStore('user', ColumnNames.Name);
  const localTableData = tableStore.get();
  let dataHandler = new DataHandler<Row>([], {
    rowsPerPage: localTableData.pageSize,
    totalRows: 1,
  });
  let selectedFilters: string[] = [];
  const rows = dataHandler.getRows();

  dataHandler.applySort({
    orderBy: localTableData.sort.orderBy,
    direction: localTableData.sort.direction,
  });
  localTableData.filters.kvp?.map((x) => {
    dataHandler.filter(x.value, x.key);
    selectedFilters = [...selectedFilters, x.key];
  });
  if (localTableData.filters.search) {
    dataHandler.filter(localTableData.filters.search, ColumnNames.Name);
  }

  dataHandler.onChange(async (state: State) => {
    const result = await userStore.fetch(clientId, state);
    const total: number = result.total > 0 ? result.total : 1;
    dataHandler.setTotalRows(total);
    return result.data;
  });
  dataHandler.invalidate();

  let searchValue: string | null = '';
  let searchByPermission: boolean = false;
  let timeout: any;

  $: searchValue = $searchStore.search;
  $: searchByPermission = $searchByPermissionStore.checked;
  $: orderByValue = $sortingStore.orderBy;
  $: sortByValue = $sortingStore.sortBy;

  onMount(async () => {
    dataHandler.onChange(async (state: State) => {
      const result = await userStore.fetch(clientId, state);
      const total: number = result.total > 0 ? result.total : 1;
      dataHandler.setTotalRows(total);
      return result.data || [];
    });
    dataHandler.invalidate();
  });

  function onSearchValueChange() {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      // handle searching by client id or by name
      dataHandler.clearFilters();
      if (isStringInt(searchValue)) {
        dataHandler.filter(searchValue, ColumnNames.Id);
      } else if (searchByPermission) {
        dataHandler.filter(searchValue, ColumnNames.Permission);
      } else {
        dataHandler.filter(searchValue, ColumnNames.Name);
      }
      dataHandler.invalidate();
    }, 400);
  }

  $: if (searchValue !== undefined) {
    onSearchValueChange();
  }
  $: if (searchByPermission !== undefined) {
    onSearchValueChange();
  }

  $: if (orderByValue) {
    onItemSelect(orderByValue, sortByValue);
  }

  function onItemSelect(orderBy: string, sortBy: 'asc' | 'desc') {
    dataHandler.applySort({ orderBy, direction: sortBy });
    dataHandler.invalidate();
  }

  let dropdownOpen = false;

  function toggleDropdown(event) {
    const currentTarget = event.currentTarget;
    currentTarget.nextElementSibling.classList.toggle('hidden');
  }

  async function handleEdit(row: Row): Promise<void> {
    setUIState('UpdateUserPage');
    setUserId(row.id);
  }

  async function toggleActiveStatus(row) {
    const updatedStatus = !row.isActive;
    await userStore.updateActiveStatus(row.id, updatedStatus);
    dataHandler.invalidate();
  }

  const handleEmulateClick = (row) => {
    userStore.getUserEmulationLink(row.id);
  };
  
  function isStringInt(value: string): boolean {
    if (!value || !/^-?\d+$/.test(value)) {
      return false;
    }
    const parsed = Number(value);
    return Number.isSafeInteger(parsed);
  }
</script>

<div class="ml-5 mr-5 mt-5 rounded-3xl border-2 border-surface-600 bg-white bg-opacity-90 p-4 lg:ml-0">
  <div class="mx-auto flex flex-col p-4">
    <div class="table-container h-auto overflow-x-auto rounded-2xl rounded-bl-none rounded-br-none border border-b-0 border-[--tableBorder]">
      <table class="table table-hover table-auto rounded-2xl rounded-bl-none rounded-br-none lg:table-fixed">
        <thead class="!bg-[--tableHeaderBackground]">
          <tr>
            <th class="!text-md h-[39px] w-[10%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]"> User Id </th>
            <th class="!text-md h-[39px] w-[15%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]"> Name </th>
            <th class="!text-md h-[39px] w-[25%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]"> Email </th>
            <th class="!text-md h-[39px] w-[35%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]"> Permissions </th>
            <th class="!text-md h-[39px] w-[15%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]"> Emulation </th>
          </tr>
        </thead>
        <tbody>
          {#each $rows as row}
            <tr class="!border-[--tableBorder]">
              <td class="h-10 w-[10%] content-center !px-5">
                <div class="text-md flex items-center font-normal text-[--tableBodyFont]">
                  {row.id}
                </div>
              </td>
              <td class="w-[15%] cursor-pointer content-center !px-5">
                <div class="text-md center flex items-center font-bold text-primary-500">
                  {row.name}
                </div>
              </td>
              <td class="w-[25%] content-center !px-5">
                <div class="text-md flex items-center font-normal text-[--tableBodyFont]">
                  {row.email}
                </div>
              </td>
              <td class="w-[35%] content-center !px-5">
                <div class="text-md flex items-center font-normal text-primary-500">
                  {row.permissions}
                </div>
              </td>

              <td class="relative w-[15%] content-center">
                <div class="flex items-center justify-end gap-2">
                  {#if row.isActive}
                    <button
                      class="btn-outline btn btn-lg flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-100"
                      on:click={() => handleEmulateClick(row)}
                    >
                      <Ghost class="text-blue-500" size="24" />
                      <span class="font-semibold text-primary-700">Emulate</span>
                    </button>
                  {/if}
                  <div class="relative">
                    <button class="focus:outline-none" on:click={toggleDropdown}>
                      <DotsThreeOutlineVertical size={24} class="text-gray-500" />
                    </button>
                    <div
                      use:clickOutside={{ callback: () => (dropdownOpen = false) }}
                      class="dropdown-menu {dropdownOpen ? '' : 'hidden'} absolute right-0 z-50 mt-2 w-40 rounded-lg border border-gray-300 bg-white shadow-lg"
                    >
                      <ul>
                        <li class="flex w-full cursor-pointer items-center gap-2 p-2 hover:bg-gray-100">
                          <PencilSimple class="text-orange-500" size={20} />
                          <button class="w-full text-left text-gray-700" on:click={() => handleEdit(row)}>Edit</button>
                        </li>
                        <li class="flex w-full cursor-pointer items-center gap-2 p-2 hover:bg-gray-100">
                          {#if row.isActive}
                            <EyeSlash class="text-gray-500" size={20} />
                          {:else}
                            <Eye class="text-green-500" size={20} />
                          {/if}
                          <button class="w-full text-left text-gray-700" on:click={() => toggleActiveStatus(row)}>
                            {row.isActive ? 'Make Inactive' : 'Make Active'}
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
    <div class="mt-5 inline-flex w-full justify-between">
      <RowsPerPage handler={dataHandler} store={tableStore} />
      <Pagination handler={dataHandler}></Pagination>
    </div>
  </div>
</div>

<style>
  .dropdown-menu {
    position: absolute;
    background-color: white;
    border: 1px solid #ccc;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    padding: 8px;
    z-index: 1000;
  }
</style>
